import { Injectable } from '@angular/core';

export interface PaginationResult<T> {
  newRows: T[];
  hasMoreRows: boolean;
  currentPage: number;
  filteredRows: T[];
}

export interface FilterFunction<T> {
  (filter: any, rows: T[]): T[];
}

export interface SelectionCallback<T> {
  (): void;
}

export interface GridApi {
  forEachNodeAfterFilter: (callback: (node: any) => void) => void;
}

export interface SelectionData<T> {
  selectedItems: T[];
  identifierField: string;
}

@Injectable({
  providedIn: 'root'
})
export class PaginationHelperService {

  constructor() { }

  /**
   * Generic method to process paginated data with filtering
   * @param newRows - The new rows from the API
   * @param currentPage - Current page number
   * @param pageSize - Number of rows per page
   * @param filterInput - Filter object containing search criteria
   * @param filterFunction - Function to apply filtering
   * @param accumulatedFilteredRows - Previously accumulated filtered rows
   * @returns PaginationResult with processed data
   */
  processPaginatedData<T>(
    newRows: T[],
    currentPage: number,
    pageSize: number,
    filterInput: any,
    filterFunction: FilterFunction<T>,
    accumulatedFilteredRows: T[] = []
  ): PaginationResult<T> {
    
    const returnedRowCount = newRows.length;
    let hasMoreRows = true;
    let nextPage = currentPage;

    // Determine if there are more rows
    if (returnedRowCount < pageSize) {
      hasMoreRows = false;
    } else {
      hasMoreRows = true;
      nextPage = currentPage + 1;
    }

    let processedRows = newRows;
    let filteredRows = accumulatedFilteredRows;

    // Apply filtering if filter input exists
    if (filterInput && filterInput.textoBuscar !== '') {
      filteredRows = [...filteredRows, ...filterFunction(filterInput, newRows)];

      if (filteredRows.length < pageSize) {
        if (hasMoreRows) {
          // Need to load more data to satisfy filter
          return {
            newRows: [],
            hasMoreRows: true,
            currentPage: nextPage,
            filteredRows: filteredRows
          };
        } else {
          // No more data, use filtered results
          processedRows = filteredRows;
        }
      } else {
        // Enough filtered results
        processedRows = filteredRows;
      }
    }

    return {
      newRows: processedRows,
      hasMoreRows: hasMoreRows,
      currentPage: nextPage,
      filteredRows: filteredRows
    };
  }

  /**
   * Generic method to re-select previously selected rows in a grid
   * @param gridApi - The grid API instance
   * @param selectionData - Object containing selected items and identifier field
   */
  selectPreviouslySelectedRows<T>(
    gridApi: GridApi,
    selectionData: SelectionData<T>
  ): void {
    if (selectionData.selectedItems && selectionData.selectedItems.length > 0) {
      gridApi.forEachNodeAfterFilter((node: any) => {
        const index = selectionData.selectedItems.findIndex(
          (item: any) => item[selectionData.identifierField] === node.data[selectionData.identifierField]
        );
        if (index > -1) {
          node.setSelected(true);
        }
      });
    }
  }
}
